import type { HttpClient } from '@angular/common/http';
import type { FormGroup } from '@angular/forms';
import type { Observable, Subject, Subscription } from 'rxjs';
import { auditTime, takeUntil } from 'rxjs/operators';
import type { Form, FormField } from '../../interfaces/form.interface';
import type { ReactiveFieldRuntimeState } from '../../interfaces/reactive-field-runtime-state.interface';
import { ReactiveFieldRuntimeStateManager } from '../../model/reactive-field-runtime-state.manager';
import type { ReactiveChangeOrigin } from '../change-origin/reactive-change-origin';
import { ReactiveDropdownCacheService } from '../reactive-dropdown-cache.service';
import {
  ReactiveOptionStoreService,
  type ReactiveOptionStoreEntry,
} from '../reactive-option-store.service';
import { ReactiveDropdownEngine, type ReactiveDropdownDependencyLoadOptions } from '../dropdown/reactive-dropdown-engine';
import type { ReactiveFormBuilderStorageAdapter } from '../../adapters/reactive-form-builder.adapters';

export interface ReactiveFormDropdownFacadeConfiguration<T> {
  readonly http: HttpClient;
  readonly dropdownCache: ReactiveDropdownCacheService;
  readonly optionStore: ReactiveOptionStoreService;
  readonly getForm: () => Form<T> | null;
  readonly setForm: (form: Form<T>) => void;
  readonly getModel: () => FormGroup | null;
  readonly getPreviousData: () => T | null;
  readonly getServerUrl: () => string;
  readonly getAppName: () => string;
  readonly canMutateModelSafely: () => boolean;
  readonly isDestroyed: () => boolean;
  readonly applySelectedValue: (field: FormField<T>, value: unknown, origin: ReactiveChangeOrigin) => void;
  readonly applyResolvedFieldData: (fieldName: string, origin: ReactiveChangeOrigin) => Promise<void>;
  readonly completeFieldDataCascade: (fieldName: string, origin: ReactiveChangeOrigin) => void;
  readonly publishStableValueSnapshot: (fieldName: string, origin: ReactiveChangeOrigin) => void;
  readonly notifyStateChanged: () => void;
  readonly markExternalDropdownsReady: (origin: ReactiveChangeOrigin) => void | Promise<void>;
  readonly storage?: ReactiveFormBuilderStorageAdapter | undefined;
}

const DROPDOWN_TRIGGER_AUDIT_MS = 50;

/**
 * Centralizes dropdown engine ownership, external dropdown readiness and progressive refresh trigger wiring.
 */
export class ReactiveFormDropdownFacade<T> {
  private readonly engine: ReactiveDropdownEngine<T>;
  private externalDropdownsReady = false;
  private dropdownTriggerSub: Subscription | null = null;

  constructor(private readonly configuration: ReactiveFormDropdownFacadeConfiguration<T>) {
    this.engine = new ReactiveDropdownEngine<T>({
      http: this.configuration.http,
      dropdownCache: this.configuration.dropdownCache,
      optionStore: this.configuration.optionStore,
      getForm: () => this.configuration.getForm(),
      setForm: (form) => this.configuration.setForm(form),
      getModel: () => this.configuration.getModel(),
      getPreviousData: () => this.configuration.getPreviousData(),
      getServerUrl: () => this.configuration.getServerUrl(),
      getAppName: () => this.configuration.getAppName(),
      canMutateModelSafely: () => this.configuration.canMutateModelSafely(),
      isDestroyed: () => this.configuration.isDestroyed(),
      applySelectedValue: (field, value, origin) => this.configuration.applySelectedValue(field, value, origin),
      applyResolvedFieldData: (fieldName, origin) => this.configuration.applyResolvedFieldData(fieldName, origin),
      completeFieldDataCascade: (fieldName, origin) => this.configuration.completeFieldDataCascade(fieldName, origin),
      publishStableValueSnapshot: (fieldName, origin) => this.configuration.publishStableValueSnapshot(fieldName, origin),
      notifyStateChanged: () => this.configuration.notifyStateChanged(),
      storage: this.configuration.storage,
    });
  }

  public destroy(): void {
    this.dropdownTriggerSub?.unsubscribe();
    this.dropdownTriggerSub = null;
    this.engine.destroy();
  }

  public areExternalDropdownsReady(): boolean {
    return this.externalDropdownsReady;
  }

  public resetExternalDropdowns(): void {
    this.externalDropdownsReady = false;
  }

  public async finalizeExternalDropdowns(origin: ReactiveChangeOrigin): Promise<void> {
    this.externalDropdownsReady = true;
    await this.configuration.markExternalDropdownsReady(origin);
  }

  public registerTrigger(
    trigger: Observable<void> | null,
    destroy$: Subject<void>,
    refreshMappings: () => void,
  ): void {
    this.dropdownTriggerSub?.unsubscribe();
    this.dropdownTriggerSub = null;

    if (!trigger) {
      return;
    }

    this.dropdownTriggerSub = trigger
      .pipe(
        auditTime(DROPDOWN_TRIGGER_AUDIT_MS),
        takeUntil(destroy$),
      )
      .subscribe(() => {
        this.externalDropdownsReady = true;
        refreshMappings();
      });
  }

  public buildFieldRuntimeStates(): ReadonlyMap<string, ReactiveFieldRuntimeState<T>> {
    const form = this.configuration.getForm();

    if (!form) {
      return new Map();
    }

    return ReactiveFieldRuntimeStateManager.build(
      form,
      this.configuration.getModel(),
      this.externalDropdownsReady,
      this.engine.getFieldLoadStates(),
      this.engine.getFieldErrors(),
      this.engine.getFieldWarnings(),
      this.engine.getFieldDiagnostics(),
    );
  }

  public hasLoadingDropdowns(fieldStates: ReadonlyMap<string, ReactiveFieldRuntimeState<T>>): boolean {
    return ReactiveFieldRuntimeStateManager.hasLoadingDropdowns(fieldStates);
  }

  public capturePendingSelectionsFromPreviousData(): void {
    this.engine.capturePendingSelectionsFromPreviousData();
  }

  public capturePendingSelectionsFromCurrentModel(): void {
    this.engine.capturePendingSelectionsFromCurrentModel();
  }

  public resolvePendingSelections(): void {
    this.engine.resolvePendingSelections();
  }

  public restoreCache(): void {
    this.engine.restoreCache();
  }

  public saveCache(): void {
    this.engine.saveCache();
  }

  public async refresh(fieldName: string, options: ReactiveDropdownDependencyLoadOptions): Promise<void> {
    await this.engine.refresh(fieldName, options);
  }

  public async loadOnDemand(fieldName: string, options: ReactiveDropdownDependencyLoadOptions): Promise<void> {
    await this.engine.loadOnDemand(fieldName, options);
  }

  public async loadInitial(): Promise<void> {
    await this.engine.loadInitial();
  }

  public async loadHydratedDependencies(): Promise<void> {
    const dependencyParentFields = this.engine.getHydratedDependencyParents();

    if (dependencyParentFields.length === 0) {
      return;
    }

    await this.engine.loadDependent(dependencyParentFields, { isHydration: true, origin: 'hydrate' });
  }

  public async loadDependent(
    changedFields: readonly string[],
    options?: ReactiveDropdownDependencyLoadOptions,
  ): Promise<void> {
    await this.engine.loadDependent(changedFields, options);
  }

  public resolveRawValue<TEntity = unknown>(fieldName: string, sourceValue: unknown): TEntity | null {
    return this.engine.resolveRawValue<TEntity>(fieldName, sourceValue);
  }

  public resolveOption(fieldName: string, sourceValue: unknown): ReactiveOptionStoreEntry | null {
    return this.engine.resolveOption(fieldName, sourceValue);
  }

  public resolveLabel(fieldName: string, sourceValue: unknown): string {
    return this.engine.resolveLabel(fieldName, sourceValue);
  }

  public resolveBackendValue(fieldName: string, sourceValue: unknown): unknown | null {
    return this.engine.resolveBackendValue(fieldName, sourceValue);
  }
}
